import React from 'react';
import { RatingType } from './RatingType';
import { ShareTargetType } from './ShareTargetType';
import { NominationAction } from './useNominationTargets';
import api from '../../../../Api';
import { useUser } from '../../../User/containers/UserDataStore';

export default function useEditorController(
	sku,
	onUpdate,
	reviewEditor,
	ratingsDataEditor,
	shareTargetsEditor,
	nominationTargetsEditor,
) {
	const {
		state: {
			user: { user },
		},
	} = useUser();

	const [didSaveEditor, setDidSaveEditor] = React.useState(false);

	const buildAssessmentIdsString = () => {
		const orderedTypes = [
			RatingType.overall.toString(),
			RatingType.writingQuality.toString(),
			RatingType.imageIllustrationQuality.toString(),
			RatingType.characterDevelopment.toString(),
			RatingType.couldntPutItDownness.toString(),
			RatingType.intellectualDepth.toString(),
			RatingType.originality.toString(),
		];
		return orderedTypes.join();
	};

	const getRatingValue = ratingType => {
		const rating = ratingsDataEditor.get(ratingType);

		if (typeof rating.value === 'number') {
			return rating.value.toString();
		}
		return RatingType.none.toString();
	};

	const buildAssessmentValuesString = fallbackOverallRating => {
		const getValueForOverallRating = () => {
			const overallValue = ratingsDataEditor.get(RatingType.overall)
				.value;

			if (!fallbackOverallRating || overallValue !== RatingType.none) {
				return getRatingValue(RatingType.overall);
			}

			return typeof fallbackOverallRating === 'number'
				? fallbackOverallRating.toString()
				: getRatingValue(RatingType.overall);
		};

		const orderedValues = [
			getValueForOverallRating(),
			getRatingValue(RatingType.writingQuality),
			getRatingValue(RatingType.imageIllustrationQuality),
			getRatingValue(RatingType.characterDevelopment),
			getRatingValue(RatingType.couldntPutItDownness),
			getRatingValue(RatingType.intellectualDepth),
			getRatingValue(RatingType.originality),
		];
		return orderedValues.join();
	};

	const buildShareTargetsString = () => {
		const shareTargetsToSave = [];

		const publisherShareTargetIdentifier = '0';

		if (shareTargetsEditor.isActive({ type: ShareTargetType.publisher })) {
			shareTargetsToSave.push(publisherShareTargetIdentifier);
		}

		return shareTargetsToSave.join();
	};

	const buildSubmitToNominations = () => {
		const ids = nominationTargetsEditor.getSelectedNominationAffiliationIds();
		return ids.map(id => id.toString()).join();
	};

	const buildAssessmentDataToSave = fallbackOverallRating => ({
		sku,
		assessmentIds: buildAssessmentIdsString(),
		assessmentIdValues: buildAssessmentValuesString(fallbackOverallRating),
		review: encodeURI(reviewEditor.getEditorValue()),
		share: buildShareTargetsString(), // ReviewSubmissionType, but Publisher is the only one actually used!
		submitTo: buildSubmitToNominations(), // Nominate section. These are referred to as "Submission Lists"
		saveAsDefault: false,
	});

	// { communityType: 2 }, // Colleagues
	// { communityType: 3 }, // Friends
	const buildColleaguesAndFriendsShareExceptionBody = () => {
		const shareTypesToExclude = [];

		const friendsCommunityType = 3;
		const colleaguesCommunityType = 2;

		if (!shareTargetsEditor.isActive({ type: ShareTargetType.friends })) {
			shareTypesToExclude.push({ communityType: friendsCommunityType });
		}

		if (
			!shareTargetsEditor.isActive({ type: ShareTargetType.colleagues })
		) {
			shareTypesToExclude.push({
				communityType: colleaguesCommunityType,
			});
		}

		return {
			CommunityShareTypes: shareTypesToExclude,
			SaveAsDefault: false,
		};
	};

	const buildGeneralCommunitiesShareExceptionBody = () => {
		const { getCommunityIdsToNotShareWith } = shareTargetsEditor;
		const communityIdsToNotShareWith = getCommunityIdsToNotShareWith();
		return {
			saveAsDefault: false,
			AffiliationShareTypes: communityIdsToNotShareWith.map(id => ({
				targetGroupId: id,
				isUnchecked: true,
			})),
		};
	};

	const save = async (
		options = { fallbackOverallRating: null, doSubmitSharingState: true },
	) => {
		const userId = user.id;
		// 'join' any genre communities that the user is NOT already a member of AND that is a "selected" share target
		// PUT: api/affiliations/me/{affiliationId}/user
		const genreCommunityIdsToJoin = shareTargetsEditor.getChosenGenreCommunityIdsToJoin();

		const joinRequests = genreCommunityIdsToJoin.map(id =>
			api.put(`/api/affiliations/me/${id}/user`),
		);

		if (joinRequests.length) {
			await Promise.all(joinRequests);
		}

		await api.put(
			`/api/me/reviews/products/${sku}`,
			buildAssessmentDataToSave(options.fallbackOverallRating),
		);

		if (options.doSubmitSharingState) {
			// PUT if Colleagues and Friends are 'excluded' from the share
			await api.put(
				`/api/products/${sku}/users/${userId}/communities/shares-exceptions`,
				buildColleaguesAndFriendsShareExceptionBody(),
			);

			// POST the Communities (affiliations that the assessment should NOT be shared with. This includes genre communities)
			await api.post(
				`/api/products/${sku}/users/${userId}/communities/shares-exceptions/affiliations`,
				buildGeneralCommunitiesShareExceptionBody(),
			);
		}

		// There will never be a general "Community"/"Affiliation" share type in the new UI, so we can always delete it in case the old review modal is still used
		await api.del(
			`/api/products/${sku}/users/${userId}/communities/shares-exceptions`,
			[{ CommunityType: 4 }],
		);

		setDidSaveEditor(true);

		nominationTargetsEditor.update({
			type: NominationAction.NOMINATIONS_SUBMITTED,
		});

		if (typeof onUpdate === 'function') {
			await onUpdate();
		}
	};

	const removeReviewAssessment = async () => {
		const url = `${process.env.REACT_APP_EPLUS_REQUEST_URL}/getJSONData.aspx?builder=ClearBuyerAssessment&sku=${sku}`;

		await fetch(url, {
			method: 'GET',
			credentials: 'include',
		});

		if (typeof onUpdate === 'function') {
			await onUpdate();
		}
	};

	return {
		save,
		remove: removeReviewAssessment,
		doesReviewExist: () => reviewEditor.doesReviewExist(),
		didSave: () => didSaveEditor,
		reset: () => setDidSaveEditor(false),
	};
}
